import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthService from '../../services/authService';
import { logger } from '../../utils/logger';
import config from '../../config/api.config';
import { isDeskAdmin } from '../../utils/authHelpers';

const PROCESSED_HANDOFF_KEY = 'processed_env_handoff_keys';
/** Module-level guard so React Strict Mode cannot double-consume a one-time refresh key. */
const inFlightHandoffKeys = new Set<string>();

const EnvHandoffHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasProcessed, setHasProcessed] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const refreshKey =
      searchParams.get('refreshKey') || searchParams.get('refreshToken');

    if (!refreshKey) {
      logger.info('No refreshKey on env-handoff route', 'EnvHandoffHandler');
      if (config.isStagingHost()) {
        AuthService.redirectToLogin();
      } else {
        navigate('/', { replace: true });
      }
      return;
    }

    const processed = sessionStorage.getItem(PROCESSED_HANDOFF_KEY) || '';
    if (processed.includes(refreshKey)) {
      // Already finished this key — if tokens exist, continue into the app.
      if (AuthService.getToken() && AuthService.getRefreshToken()) {
        logger.info('Handoff key already processed with tokens present', 'EnvHandoffHandler');
        navigate('/dashboard/main', { replace: true });
        return;
      }
      logger.info('Handoff key already processed without tokens', 'EnvHandoffHandler');
      setError('این لینک ورود قبلاً استفاده شده است.');
      setIsProcessing(false);
      setTimeout(() => {
        if (config.isStagingHost()) {
          window.location.replace(config.INSIGHT_PROD_URL);
        } else {
          navigate('/', { replace: true });
        }
      }, 3000);
      return;
    }

    if (inFlightHandoffKeys.has(refreshKey) || hasProcessed) {
      return;
    }

    const processHandoff = async () => {
      setHasProcessed(true);
      inFlightHandoffKeys.add(refreshKey);

      const brandIdParam = searchParams.get('brandId');
      const branchIdParam = searchParams.get('branchId');
      const brandId = brandIdParam ? parseInt(brandIdParam, 10) : undefined;
      const branchId = branchIdParam ? parseInt(branchIdParam, 10) : undefined;

      if (brandIdParam && brandIdParam !== '0' && brandId !== undefined && !Number.isNaN(brandId)) {
        AuthService.saveBrandSelection(brandId);
      }
      if (branchIdParam && branchIdParam !== '0' && branchId !== undefined && !Number.isNaN(branchId)) {
        AuthService.saveBranchSelection(branchId);
      }

      let exchangeSucceeded = false;

      try {
        await AuthService.exchangeRefreshKey(refreshKey);
        exchangeSucceeded = true;

        if (!AuthService.getRefreshToken()) {
          throw new Error('Refresh token was not persisted after env-handoff exchange');
        }

        try {
          const userInfo = await AuthService.fetchUserInfo(brandId, branchId);
          if (config.isStagingHost() && !isDeskAdmin(userInfo)) {
            AuthService.clearAuth();
            setError('دسترسی به محیط staging فقط برای مدیر سیستم مجاز است.');
            setIsProcessing(false);
            setTimeout(() => {
              window.location.replace(config.INSIGHT_PROD_URL);
            }, 3000);
            return;
          }
        } catch (userInfoError) {
          // Tokens are already valid — do not wipe them if Desk user-info is flaky.
          logger.error(
            'User info fetch failed after handoff; continuing with tokens',
            'EnvHandoffHandler',
            userInfoError
          );
        }

        sessionStorage.setItem(
          PROCESSED_HANDOFF_KEY,
          `${processed},${refreshKey}`.slice(-4000)
        );

        await new Promise((resolve) => setTimeout(resolve, 100));
        navigate('/dashboard/main', { replace: true });
      } catch (handoffError) {
        logger.error('Environment handoff failed', 'EnvHandoffHandler', handoffError);
        // Only clear when exchange itself failed; keep tokens if a later step broke.
        if (!exchangeSucceeded) {
          AuthService.clearAuth();
        }
        setError('ورود بین محیط‌ها با خطا مواجه شد.');
        setIsProcessing(false);
        setTimeout(() => {
          if (AuthService.getToken() && AuthService.getRefreshToken()) {
            navigate('/dashboard/main', { replace: true });
            return;
          }
          if (config.isStagingHost()) {
            window.location.replace(config.INSIGHT_PROD_URL);
          } else {
            navigate('/', { replace: true });
          }
        }, 3000);
      } finally {
        inFlightHandoffKeys.delete(refreshKey);
      }
    };

    processHandoff();
  }, [location, navigate, hasProcessed]);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4 text-red-600">{error}</h2>
          <p className="text-gray-600">در حال انتقال…</p>
        </div>
      </div>
    );
  }

  if (isProcessing) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">در حال ورود به محیط…</h2>
          <div className="animate-loading-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        </div>
      </div>
    );
  }

  return null;
};

export default EnvHandoffHandler;
